import type { DateTime } from 'luxon';
import type { HtmlFromDotcms } from './converters-from-dotcms';
import {
  convertDotcmsBoolean,
  convertDotcmsDateTime,
  convertDotcmsEnumValue,
  convertDotcmsEnumValues,
  convertDotcmsResponseToRecord,
  wrapDotcmsHtml,
} from './converters-from-dotcms';
import type { IGqlHelper } from './gql-helper';
import { AlertBannerVariant } from '../../component_library/components/AlertBanner/AlertBanner';
import type { IBaseObjectData } from './dotcms-base-object';
import type { Page } from '../../app/page-url-path';
import { getDisplayOnPages } from './dotcms-pages-category';
import {
  AlertDisplayConditionTime,
  AlertDisplayConditionUserState,
  AlertTriggerToDisplay,
} from './dotcms-alert-enums';
import { CategoryGqlHelper } from './dotcms-category';
import { processGqlDataAsArray } from '../use-builders-to-process-gql-data';
import { buildGqlFieldsQuery } from './gql-builders';
import { DotKeyValueGqlHelper } from './dotcms-dotkeyvalue';
import { processAllowOrBlockList } from './dotcms-item-restricted-based-on-country-jurisdiction';

export type IAlertData = IBaseObjectData & {
  text: HtmlFromDotcms;
  alertTheme: AlertBannerVariant;
  isClosable: boolean;
  displayConditionsPage: Page[];
  triggerToDisplayAlert: AlertTriggerToDisplay[];
  displayConditionsUserState?: AlertDisplayConditionUserState[];
  displayConditionsTime: AlertDisplayConditionTime[];
  durationFrom?: DateTime;
  durationTo?: DateTime;
};

export const AlertGqlHelper: IGqlHelper<IAlertData> = {
  contentTypeName: 'Alert',
  gqlFieldsQuery: `
    text
    alerttheme
    isClosable
    displayConditionsPage { ${buildGqlFieldsQuery(CategoryGqlHelper)} }
    triggerToDisplayAlert
    displayConditionsUserState
    displayConditionsTime
    durationFrom
    durationTo
    countryAllowList { ${DotKeyValueGqlHelper.gqlFieldsQuery} }
    countryBlockList { ${DotKeyValueGqlHelper.gqlFieldsQuery} }
  `,
  processGqlData: (gqlResponse: unknown) => {
    if (!gqlResponse) {
      return undefined;
    }

    const {
      text,
      alerttheme,
      isClosable,
      displayConditionsPage,
      triggerToDisplayAlert,
      displayConditionsUserState,
      displayConditionsTime,
      durationFrom,
      durationTo,
      countryAllowList,
      countryBlockList,
    } = convertDotcmsResponseToRecord(gqlResponse);

    return {
      text: wrapDotcmsHtml(text),
      alertTheme:
        convertDotcmsEnumValue(AlertBannerVariant, alerttheme) ??
        AlertBannerVariant.Default,
      isClosable: convertDotcmsBoolean(isClosable) ?? true,
      displayConditionsPage: getDisplayOnPages(
        processGqlDataAsArray(
          CategoryGqlHelper,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          displayConditionsPage,
        ),
      ),
      triggerToDisplayAlert:
        convertDotcmsEnumValues(AlertTriggerToDisplay, triggerToDisplayAlert) ??
        [],
      displayConditionsUserState: convertDotcmsEnumValues(
        AlertDisplayConditionUserState,
        displayConditionsUserState,
      ),
      displayConditionsTime: convertDotcmsEnumValues(
        AlertDisplayConditionTime,
        displayConditionsTime,
      ) ?? [AlertDisplayConditionTime.SHOW_ALWAYS],
      durationFrom: convertDotcmsDateTime(durationFrom),
      durationTo: convertDotcmsDateTime(durationTo),
      countryAllowList: processAllowOrBlockList(
        processGqlDataAsArray(
          DotKeyValueGqlHelper,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          countryAllowList,
        ),
      ),
      countryBlockList: processAllowOrBlockList(
        processGqlDataAsArray(
          DotKeyValueGqlHelper,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          countryBlockList,
        ),
      ),
    };
  },
};
